import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MessagingContext = createContext(null);
export const useMessaging = () => useContext(MessagingContext);

/**
 * Holds the unread-notification summary and a realtime websocket connection.
 * `tick` increments on every inbound realtime event so inbox/bell views can
 * cheaply re-fetch. Active only while a user is authenticated.
 */
export function MessagingProvider({ children }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState({ unread: 0, patients: 0, doctors: 0 });
  const [tick, setTick] = useState(0);
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const retryRef = useRef(null);

  const refreshSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/summary");
      setSummary(data);
    } catch { /* not authed yet */ }
  }, []);

  useEffect(() => {
    if (!user) {
      setSummary({ unread: 0, patients: 0, doctors: 0 });
      return;
    }
    let closed = false;
    refreshSummary();

    const connect = () => {
      if (closed) return;
      const tok = getToken();
      const wsUrl = API.replace(/^http/, "ws") + "/ws/notifications" + (tok ? `?token=${encodeURIComponent(tok)}` : "");
      let socket;
      try { socket = new WebSocket(wsUrl); } catch { return; }
      wsRef.current = socket;
      socket.onmessage = () => { refreshSummary(); setTick((t) => t + 1); };
      socket.onopen = () => {
        clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          try { if (socket.readyState === 1) socket.send("ping"); } catch { /* ignore */ }
        }, 25000);
      };
      socket.onclose = () => {
        clearInterval(pingRef.current);
        if (!closed) retryRef.current = setTimeout(connect, 3000);
      };
    };
    connect();

    return () => {
      closed = true;
      clearTimeout(retryRef.current);
      clearInterval(pingRef.current);
      try { wsRef.current && wsRef.current.close(); } catch { /* ignore */ }
    };
  }, [user?.id, refreshSummary]);

  return (
    <MessagingContext.Provider value={{ summary, refreshSummary, tick }}>
      {children}
    </MessagingContext.Provider>
  );
}
